<template>
  <div class="partProgress">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="部件列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <!-- 表格 -->
      <template slot="card-box">
        <card-table
          ref="cardTableRef"
          tableID="partsTable"
          :key="tableRenderKey"
          :total="totalCounts"
          :currentPage="currentPage"
          @sizeChange="handleSizeChanges"
          @currentChange="handleCurrentChanges"
          :tableColumn="effectiveVisibleColumns"
          :tableData="partData"
          style="height: 100%;"
          :showIndex="true"
          :showSelection="false"
          :loading="loading"
          :showPaging="true"
          :size="pageSize"
          :mobileColumns="mobileColumnEditFilter"
          @column-filter-change="onColumnFilterChange"
          :columnFilterValues="columnFilterValues"
          @column-width-change="onColumnWidthChange"
          @linkClick="handleProductCodeClick"
        >
        </card-table>
      </template>

    </card-m>

    <!-- 产品部件信息弹窗（部件/自检确认/自检记录/问题列表 四表格切换） -->
    <product-part-info-dialog
      :visible="partsDialogVisible"
      :title="'产品列表 - 产品编号: ' + currentPartsProductCode"
      :productCode="currentPartsProductCode"
      :partsList="partsList"
      :inspectionList="inspectionList"
      :resInspectionList="resInspectionList"
      :issueList="partsIssueList"
      @close="closePartsDialog"
    />

    <!-- 列编辑弹窗 -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />
  </div>
</template>

<script>
import { getPartHomeList, getProductInfoList } from '@/api/mainapi.js';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import ProductPartInfoDialog from '@/components/card/ProductPartInfoDialog.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

export default {
  name: 'partProgress',
  mixins: [columnEditMixin],
  components: { ColumnEditDialog, ProductPartInfoDialog },
  props: {
    title: {
      type: String,
      default: '部件主页'
    }
  },
  data() {
    return {
      // 表格列定义
      partsListColumn: [
        {
          prop: 'partsSerialCode',
          label: '部件编号',
          filterable: true
        },
        {
          prop: 'fsn',
          label: '短代码',
          filterable: true
        },
        {
          prop: 'name',
          label: '部件名称',
          filterable: true
        },
        {
          prop: 'model',
          label: '型号',
          filterable: true
        },
        {
          prop: 'productSerialCode',
          label: '产品编号',
          filterable: true,
          custom: true,
          column: {
            type: 'link'
          }
        },
        {
          prop: 'productName',
          label: '产品名称',
          filterable: true
        },
        {
          prop: 'create_time',
          label: '创建时间',
          filterable: true,
          filterType: 'dateFrom'
        },
        {
          prop: 'update_time',
          label: '更新时间',
          filterable: true,
          filterType: 'dateFrom'
        }
      ],

      // 手机端可用列 prop 过滤（供 mixin 保存/加载手机端列偏好）
      mobileColumnFilter: [
        'partsSerialCode',
        'fsn',
        'name',
        'model',
        'productSerialCode',
        'productName',
        'create_time',
        'update_time'
      ],
      // 手机端列编辑弹窗中展示的列（可编辑范围 > 默认显示范围）
      mobileColumnEditFilter: [
        'partsSerialCode',
        'fsn',
        'name',
        'model',
        'productSerialCode',
        'productName',
        'create_time',
        'update_time'
      ],

      // 列筛选条件
      partsSerialCodeKeyword: '',
      fsnKeyword: '',
      nameKeyword: '',
      modelKeyword: '',
      productSerialCodeKeyword: '',
      productNameKeyword: '',
      createTimeKeyword: '',
      updateTimeKeyword: '',
      columnFilterValues: {},

      // 分页
      currentPage: 1,
      pageSize: 50,

      // 数据
      partData: [],
      totalCounts: 0,
      loading: false,

      // 产品部件信息弹窗（部件/自检确认/自检记录/问题列表 四表格切换）
      partsDialogVisible: false,
      partsList: [],
      inspectionList: [],
      resInspectionList: [],
      partsIssueList: [],
      currentPartsProductCode: '',

      // 列偏好页面标识（mixin 需要，自动派生 partsProgress_m）
      pageType: 'partsProgress'
    };
  },
  computed: {
    // 供 mixin 使用的列定义
    baseColumnDefs() {
      return this.partsListColumn;
    }
  },
  async mounted() {
    await this.loadColumnPref();
    this.getPartList();
  },
  methods: {
    /** 获取部件列表 */
    async getPartList() {
      this.loading = true;

      const body = {
        pageNo: String(this.currentPage),
        pageSize: String(this.pageSize),
        productSerialCode: this.productSerialCodeKeyword || '',
        productName: this.productNameKeyword || '',
        partsSerialCode: this.partsSerialCodeKeyword || '',
        partsName: this.nameKeyword || '',
        model: this.modelKeyword || '',
        fsn: this.fsnKeyword || '',
        createTime: this.createTimeKeyword || '',
        updateTime: this.updateTimeKeyword || ''
      };

      try {
        const res = await getPartHomeList(body);

        this.loading = false;

        if (res.data.code === 200) {
          const data = res.data.data;
          this.totalCounts = data.totalCount || 0;
          this.partData = this.processPartData(data.partsInfo || []);
        } else {
          this.$message.error(res.data.message || '获取部件列表失败');
        }
      } catch (error) {
        this.loading = false;
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 处理部件数据（create_time/update_time 截断到 yyyy-MM-dd，不显示时分秒） */
    processPartData(list) {
      return list.map(item => {
        return {
          partsSerialCode: item.partsSerialCode || '',
          fsn: item.fsn || '',
          name: item.name || '',
          model: item.model || '',
          productSerialCode: item.productSerialCode || '',
          productName: item.productName || '',
          create_time: this.formatDate(item.create_time),
          update_time: this.formatDate(item.update_time),
          // 保留原始数据供后续使用
          _raw: item
        };
      });
    },

    /** ✅ 日期格式化：取前 10 位（yyyy-MM-dd），不显示时分秒
     *  （与 productProgress.vue 的创建时间列口径一致）
     *  后端常见格式都会在这里收敛成日期：
     *    2026-10-21T15:30:45   → 2026-10-21
     *    2026-10-21 15:30:45   → 2026-10-21
     *    2026-10-21            → 2026-10-21（原样） */
    formatDate(val) {
      if (!val) return '';
      const str = String(val);
      return str.length >= 10 ? str.substring(0, 10) : str;
    },

    /** 列筛选变化 */
    onColumnFilterChange(filterValues) {
      this.partsSerialCodeKeyword = filterValues['partsSerialCode'] || '';
      this.fsnKeyword = filterValues['fsn'] || '';
      this.nameKeyword = filterValues['name'] || '';
      this.modelKeyword = filterValues['model'] || '';
      this.productSerialCodeKeyword = filterValues['productSerialCode'] || '';
      this.productNameKeyword = filterValues['productName'] || '';
      // dateFrom 筛选传单个日期字符串（yyyy-MM-dd）
      this.createTimeKeyword = filterValues['create_time'] || '';
      this.updateTimeKeyword = filterValues['update_time'] || '';
      this.columnFilterValues = filterValues;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getPartList();
    },

    /** 分页 */
    handleSizeChanges(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getPartList();
    },

    handleCurrentChanges(val) {
      this.currentPage = val;
      this.getPartList();
    },

    /** 导出Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.table_to_book(
        document.querySelector('#partsTable'),
        xlsxParam
      );
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '部件列表.xlsx'
        );
      } catch (e) {
        // 导出失败时静默处理，避免在无 console 环境报错
      }
      return wbout;
    },

    /** 产品编号链接点击 - 查询该产品的部件信息（部件/自检确认/自检记录） */
    async handleProductCodeClick(payload) {
      const row = payload.row;
      const serialCode = row.productSerialCode || (row._raw && row._raw.productSerialCode);
      if (!serialCode) return;

      this.currentPartsProductCode = serialCode;

      try {
        const params = {
          Type: 'ProductInformation',
          msg: serialCode
        };

        const res = await getProductInfoList(params);

        if (res.data.code === 200) {
          const data = res.data.data || {};
          const parts = data.parts || [];
          const inspection = data.inspectionList || [];
          const resInspection = data.resInspectionList || [];
          // 后端无问题时 issue 可能为字符串 '空'，做兼容处理
          const issue = Array.isArray(data.issue) ? data.issue : [];

          if (parts.length > 0 || inspection.length > 0 || resInspection.length > 0 || issue.length > 0) {
            this.partsList = parts;
            this.inspectionList = inspection;
            this.resInspectionList = resInspection;
            this.partsIssueList = issue;
            this.partsDialogVisible = true;
          } else {
            this.$message.info('该产品没有详细信息');
          }
        } else {
          this.$message.error('查询部件信息失败: ' + res.data.message);
        }
      } catch (error) {
        this.$message.error('查询部件信息失败');
      }
    },

    /** 关闭部件信息弹窗 */
    closePartsDialog() {
      this.partsDialogVisible = false;
      this.partsList = [];
      this.inspectionList = [];
      this.resInspectionList = [];
      this.partsIssueList = [];
      this.currentPartsProductCode = '';
    }
  }
};
</script>

<style scoped lang="scss">
.partProgress {
  width: 100%;
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

::v-deep .card-m {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

::v-deep .card-box {
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
  overflow-x: hidden;
}

/* 手机端 */
@media (max-width: 768px) {
  .partProgress {
    height: calc(100vh - 50px);
  }
}
</style>
